import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform, useSpring } from 'framer-motion';
import { GiWaterBottle } from 'react-icons/gi';
import { LuSprout } from 'react-icons/lu';
import { GiKneeCap } from "react-icons/gi";
import { IoFitnessSharp } from 'react-icons/io5';
import './App.css';
import logo from './assets/logo1.png';
import hero from './assets/hero.png';
import her02 from './assets/her02.png';
import who1 from './assets/who-1.png';
import who2 from './assets/who-2.png';
import who3 from './assets/who-3.png';
import benefit1 from './assets/benefit-1.png';
import benefit2 from './assets/benefit-2.png';
import benefit3 from './assets/benefit-3.png';
import benefit4 from './assets/benefit-4.png';

// Animation wrappers for scroll-triggered animations
const AnimateOnScroll = ({ children, variants, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
};

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);

  // Get scroll progress for parallax
  const { scrollYProgress } = useScroll();

  // Smooth spring physics for parallax
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Parallax effect for hero section
  const heroY = useTransform(smoothProgress, [0, 0.3], [0, 150]);

  const heroOpacity = useTransform(smoothProgress, [0, 0.3], [1, 0.5]);

  // Smooth scroll setup and loading animation
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';


    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  // Smooth scroll handler for navigation links
  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    setMobileMenuOpen(false); // Close mobile menu when clicking a link
    const element = document.querySelector(targetId);
    if (element) {
      const offset = 88; // navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Enhanced Animation variants with mobile optimization
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // Floating animation for hero image
  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="app">
      {/* NAVBAR */}
      <motion.header
        className="navbar"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="navbar-inner">
          <motion.div
            className="navbar-left"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <img className="logo-mark" src={logo} alt="Sesamune Logo" />
          </motion.div>
          <nav className="navbar-links">
            <motion.a
              href="#home"
              onClick={(e) => handleSmoothScroll(e, '#home')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Home
            </motion.a>
            <motion.a
              href="#why"
              onClick={(e) => handleSmoothScroll(e, '#why')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Why Sesamune
            </motion.a>
            <motion.a
              href="#benefits"
              onClick={(e) => handleSmoothScroll(e, '#benefits')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Benefits
            </motion.a>
            <motion.a
              href="#story"
              onClick={(e) => handleSmoothScroll(e, '#story')}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Our Story
            </motion.a>
            <motion.a
              href="#contact"
              onClick={(e) => handleSmoothScroll(e, '#contact')}
              className="nav-contact"
              whileHover={{ scale: 1.05, boxShadow: "0 4px 15px rgba(0,0,0,0.15)" }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              Contact
            </motion.a>
          </nav>

          {/* Mobile Menu Icon */}
          <motion.div
            className={`mobile-menu-icon ${mobileMenuOpen ? 'open' : ''}`}
            whileTap={{ scale: 0.9 }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </motion.div>
        </div>

        {/* Mobile Menu Overlay */}
        <motion.div
          className="mobile-menu"
          initial={{ x: '100%' }}
          animate={{ x: mobileMenuOpen ? 0 : '100%' }}
          transition={{ type: 'tween', duration: 0.3 }}
        >
          <nav className="mobile-menu-nav">
            <motion.a
              href="#home"
              onClick={(e) => handleSmoothScroll(e, '#home')}
              whileTap={{ scale: 0.95 }}
            >
              Home
            </motion.a>
            <motion.a
              href="#why"
              onClick={(e) => handleSmoothScroll(e, '#why')}
              whileTap={{ scale: 0.95 }}
            >
              Why Sesamune
            </motion.a>
            <motion.a
              href="#benefits"
              onClick={(e) => handleSmoothScroll(e, '#benefits')}
              whileTap={{ scale: 0.95 }}
            >
              Benefits
            </motion.a>
            <motion.a
              href="#story"
              onClick={(e) => handleSmoothScroll(e, '#story')}
              whileTap={{ scale: 0.95 }}
            >
              Our Story
            </motion.a>
            <motion.a
              href="#contact"
              onClick={(e) => handleSmoothScroll(e, '#contact')}
              className="mobile-nav-contact"
              whileTap={{ scale: 0.95 }}
            >
              Contact
            </motion.a>
          </nav>
        </motion.div>
      </motion.header>

      <main>
        {/* HERO SECTION */}
        <section id="home" className="hero" ref={heroRef}>
          <motion.div
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div className="hero-brand-mobile" variants={fadeInUp}>
              <motion.img
                className="logo-mark"
                src={logo}
                alt="Sesamune"
                whileHover={{ rotate: [0, -5, 5, -5, 0] }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
            <motion.h1 variants={fadeInUp}>
              Plant-Based Ancient Superfood for Modern Wellness
            </motion.h1>
            <motion.p className="hero-subtitle" variants={fadeInUp}>
              A functional wellness drink crafted from sesame seeds and jaggery &mdash;
              two ingredients trusted for over 5,000 years and refined for today&apos;s
              performance-driven life.
            </motion.p>

            <motion.ul className="hero-list" variants={staggerContainer}>
              <motion.li
                variants={fadeInUp}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg className="hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2L3 14h7l-1 8 12-14h-7l-1-6z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Naturally energizing.
              </motion.li>
              <motion.li
                variants={fadeInUp}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg className="hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Clean ingredients.
              </motion.li>
              <motion.li
                variants={fadeInUp}
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <svg className="hero-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 7v10M16 7v10M12 5v14" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Real, everyday nutrition.
              </motion.li>
            </motion.ul>

            <motion.p className="hero-tagline" variants={fadeInUp}>
              Steady energy. Strong bones. Real nourishment.
            </motion.p>

            <motion.div className="hero-actions" variants={fadeInUp}>
              <motion.button
                className="btn btn-filled"
                whileHover={{ scale: 1.05, y: -3, boxShadow: "0 8px 20px rgba(0,0,0,0.2)" }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2, type: "spring", stiffness: 400 }}
                onClick={(e) => handleSmoothScroll(e, '#story')}
              >
                Our Story
              </motion.button>
              <motion.button
                className="btn btn-outline"
                whileHover={{ scale: 1.05, y: -3, boxShadow: "0 8px 20px rgba(0,0,0,0.15)" }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2, type: "spring", stiffness: 400 }}
                onClick={(e) => handleSmoothScroll(e, '#contact')}
              >
                Contact
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-image"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: heroY, opacity: heroOpacity }}
          >
            <motion.img
              src={hero}
              alt="Sesamune Bottle"
              className="hero-bottle-img"
              whileHover={{
                scale: 1.05,
                rotate: [0, -2, 2, -2, 0],
                transition: { duration: 0.5 }
              }}
              animate={floatingAnimation}
            />
          </motion.div>
        </section>

        {/* WHY SECTION */}
        <section id="why" className="section section-why">
          <div className="section-inner">
            <AnimateOnScroll variants={fadeInLeft}>
              <div className="section-image">
                <motion.img
                  src={her02}
                  alt="Ingredients and Bottle"
                  className="bottle-card-img"
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
                  }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </AnimateOnScroll>
            <AnimateOnScroll variants={fadeInRight}>
              <div className="section-text">
                <motion.h2
                  whileInView={{ scale: [0.9, 1] }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  Why Sesamune Exists
                </motion.h2>
                <h3>Fuel Without Borrowing From Tomorrow</h3>
                <div className="section-text-content">
                  <p>Most health drinks promise quick energy, but that energy comes at a cost sudden spikes, inevitable crashes, and synthetic fixes that don&apos;t support long-term health.</p>
                  <p>Sesamune takes a different approach. It is built on the principle that real energy should be steady, not instant, and nourishing rather than overstimulating.</p>
                  <p>It follows the way traditional nutrition has always worked: slow, mineral-rich, balanced, and easy for the body to absorb.</p>
                  <p>This is not a shortcut. It&apos;s a return to fundamentals done right.</p>
                </div>
              </div>
            </AnimateOnScroll>
          </div>
        </section>

        {/* BENEFITS SECTION */}
        <section id="benefits" className="section section-benefits">
          <div className="section-inner column">
            <AnimateOnScroll variants={fadeInUp}>
              <motion.h2
                whileInView={{ scale: [0.9, 1] }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                What Makes Sesamune Different
              </motion.h2>
            </AnimateOnScroll>
            <div className="benefit-grid">

              <AnimateOnScroll variants={fadeInUp}>
                <article className="benefit-item">
                  <motion.img
                    src={benefit1}
                    alt="Nutrient Density"
                    className="benefit-image"
                    whileHover={{
                      scale: 1.05,
                      rotate: 2,
                      boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
                    }}
                    transition={{ duration: 0.4 }}
                  />
                  <motion.div
                    className="benefit-text"
                    whileInView={{ x: [30, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3>Nutrient Density You Can Measure</h3>
                    <p>Whole-food nutrition from sesame and jaggery delivering naturally occurring iron, calcium, magnesium, plant protein, healthy fats, and essential trace minerals. Clean, bioavailable nutrition your body actually absorbs.</p>
                  </motion.div>
                </article>
              </AnimateOnScroll>

              <AnimateOnScroll variants={fadeInUp}>
                <article className="benefit-item benefit-item-reverse">
                  <motion.div
                    className="benefit-text"
                    whileInView={{ x: [-30, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3>Natural Energy That Lasts</h3>
                    <p>Energy powered by sesame fats, plant protein, and natural sugars from jaggery. Delivers steady, calm energy throughout the day without jitters, spikes, or crashes.</p>
                  </motion.div>
                  <motion.img
                    src={benefit2}
                    alt="Natural Energy"
                    className="benefit-image"
                    whileHover={{
                      scale: 1.05,
                      rotate: -2,
                      boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
                    }}
                    transition={{ duration: 0.4 }}
                  />
                </article>
              </AnimateOnScroll>

              <AnimateOnScroll variants={fadeInUp}>
                <article className="benefit-item">
                  <motion.img
                    src={benefit3}
                    alt="Long Term Strength"
                    className="benefit-image"
                    whileHover={{
                      scale: 1.05,
                      rotate: 2,
                      boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
                    }}
                    transition={{ duration: 0.4 }}
                  />
                  <motion.div
                    className="benefit-text"
                    whileInView={{ x: [30, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3>Strength for the Long Term</h3>
                    <p>A balanced combination of calcium, magnesium, and manganese supports bone density, joint health, muscle function, and long-term preventive wellness.</p>
                  </motion.div>
                </article>
              </AnimateOnScroll>

              <AnimateOnScroll variants={fadeInUp}>
                <article className="benefit-item benefit-item-reverse">
                  <motion.div
                    className="benefit-text"
                    whileInView={{ x: [-30, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3>Iron That Actually Works</h3>
                    <p>Iron-rich jaggery paired with sesame&apos;s mineral profile helps improve absorption naturally. Designed for women, vegetarians, and active lifestyles without the digestive discomfort of synthetic iron.</p>
                  </motion.div>
                  <motion.img
                    src={benefit4}
                    alt="Iron absorption"
                    className="benefit-image"
                    whileHover={{
                      scale: 1.05,
                      rotate: -2,
                      boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
                    }}
                    transition={{ duration: 0.4 }}
                  />
                </article>
              </AnimateOnScroll>
            </div>
          </div>
        </section>

        {/* WHO IS FOR SECTION */}
        <section id="story" className="section section-who">
          <div className="section-inner column">
            <AnimateOnScroll variants={fadeInUp}>
              <motion.h2
                className="who-heading"
                whileInView={{ scale: [0.9, 1] }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Who Sesamune Is For
              </motion.h2>
            </AnimateOnScroll>

            <motion.div
              className="who-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.img
                src={who3}
                alt="Lifestyle 3"
                className="who-card-img"
                variants={scaleIn}
                whileHover={{
                  y: -15,
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
                }}
                transition={{ duration: 0.3 }}
              />

              <AnimateOnScroll variants={fadeInRight}>
                <p className="who-description">
                  Sesamune is thoughtfully designed for people who care about clean, everyday nutrition professionals seeking steady energy without burnout, athletes who prefer real food over powders, women who need iron that’s gentle on digestion, parents choosing better nourishment for their families, older adults focused on bone health and plant-based eaters looking for complete nutrition. Made from just whole sesame seeds and natural jaggery, Sesamune keeps ingredients simple and clean, delivering natural minerals and healthy fats that support daily energy and strength.

                  <strong>Made for: athletes and active lifestyles, vegan and plant-based diets, children and families, yoga and wellness practitioners, and older adults alike.</strong>




                </p>
              </AnimateOnScroll>

            </motion.div>
          </div>
        </section>

        {/* WHY PEOPLE CHOOSE SESAMUNE SECTION */}
        <section className="section section-why-choose">
          <div className="section-inner column">
            <AnimateOnScroll variants={fadeInUp}>
              <motion.h2
                className="why-choose-heading"
                whileInView={{ scale: [0.9, 1] }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                WHY PEOPLE CHOOSE SESAMUNE
              </motion.h2>
            </AnimateOnScroll>

            <motion.div
              className="why-choose-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              {/* Card 1 - Fuel Without the Crash */}
              <motion.article className="why-choose-card" variants={fadeInUp}>
                <motion.div
                  className="why-choose-icon-wrapper"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <GiWaterBottle className="why-choose-icon" />
                </motion.div>
                <div className="why-choose-text">
                  <h3>Fuel Without the Crash</h3>
                  <p>No stimulants or artificial boosters. Provides steady energy for training, matches and recovery.</p>
                </div>
              </motion.article>

              {/* Card 2 - Built for Endurance */}
              <motion.article className="why-choose-card" variants={fadeInUp}>
                <motion.div
                  className="why-choose-icon-wrapper"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <LuSprout className="why-choose-icon" />
                </motion.div>
                <div className="why-choose-text">
                  <h3>Built for Endurance</h3>
                  <p>Made with natural fats, plant protein and slow-release sugars for long-lasting performance without jitters.</p>
                </div>
              </motion.article>

              {/* Card 3 - Stronger Bones & Joints */}
              <motion.article className="why-choose-card" variants={fadeInUp}>
                <motion.div
                  className="why-choose-icon-wrapper"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <GiKneeCap className="why-choose-icon" />
                </motion.div>
                <div className="why-choose-text">
                  <h3>Stronger Bones & Joints</h3>
                  <p>Rich in calcium, magnesium and manganese to support bones, muscles and joint health.</p>
                </div>
              </motion.article>

              {/* Card 4 - Iron That Works */}
              <motion.article className="why-choose-card" variants={fadeInUp}>
                <motion.div
                  className="why-choose-icon-wrapper"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <IoFitnessSharp className="why-choose-icon" />
                </motion.div>
                <div className="why-choose-text">
                  <h3>Iron That Works</h3>
                  <p>Natural, easy-to-digest iron suited for athletes, women and plant-based diets.</p>
                </div>
              </motion.article>
            </motion.div>
          </div>
        </section>



        {/* TASTE EXPERIENCE SECTION */}
        <section className="section section-taste">
          <div className="section-inner column">
            <AnimateOnScroll variants={fadeInUp}>
              <motion.h2
                whileInView={{ scale: [0.9, 1] }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Taste Experience
              </motion.h2>
            </AnimateOnScroll>
            <motion.div
              className="testimonial-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.article
                className="testimonial-card"
                variants={fadeInUp}
                whileHover={{
                  y: -15,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="stars"
                  aria-hidden="true"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ★★★★★
                </motion.div>
                <p className="quote">"Sesamune fits perfectly into my daily routine. The energy feels steady and natural, without the crash I usually get from other drinks."</p>
                <p className="role">&mdash; Working Professional</p>
              </motion.article>
              <motion.article
                className="testimonial-card"
                variants={fadeInUp}
                whileHover={{
                  y: -15,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="stars"
                  aria-hidden="true"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, delay: 0.3, repeat: Infinity }}
                >
                  ★★★★★
                </motion.div>
                <p className="quote">"I prefer food-based nutrition, and Sesamune delivers exactly that. Clean ingredients, great taste, and no heaviness."</p>
                <p className="role">&mdash; Fitness Enthusiast</p>
              </motion.article>
              <motion.article
                className="testimonial-card"
                variants={fadeInUp}
                whileHover={{
                  y: -15,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="stars"
                  aria-hidden="true"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, delay: 0.6, repeat: Infinity }}
                >
                  ★★★★★
                </motion.div>
                <p className="quote">"Finding iron that doesn&apos;t upset digestion was difficult. Sesamune has made a real difference for me."</p>
                <p className="role">&mdash; Customer Review</p>
              </motion.article>
            </motion.div>
          </div>
        </section>
      </main>

      {/* CONTACT SECTION */}
      <section className="section section-contact">
        <div className="section-inner column">
          <AnimateOnScroll variants={fadeInUp}>
            <motion.h2
              className="contact-heading"
              whileInView={{ scale: [0.9, 1] }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Get In Touch
            </motion.h2>
            <p className="contact-subheading">
              We'd love to hear from you. Reach out to us through any of the channels below.
            </p>
          </AnimateOnScroll>

          <motion.div
            className="contact-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {/* Email Card */}
            <motion.a
              href="mailto:sesamune.wellness@gmail.com"
              className="contact-card"
              variants={fadeInUp}
              whileHover={{
                y: -10,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
              }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="contact-icon"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 6l-10 7L2 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
              <h3>Email</h3>
              <p>sesamune.wellness@gmail.com</p>
            </motion.a>

            {/* Phone Card 1 */}
            <motion.a
              href="tel:+918722610066"
              href="tel:+919663497122"
              className="contact-card"
              variants={fadeInUp}
              whileHover={{
                y: -10,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
              }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="contact-icon"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
              <h3>Phone</h3>
              <p>+91 87226 10066</p>
              <p>+91 96634 97122</p>
              <p>+91 98444 68159</p>
            </motion.a>

            {/* Instagram Card */}
            <motion.a
              href="https://www.instagram.com/sesamune/"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-card"
              variants={fadeInUp}
              whileHover={{
                y: -10,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
              }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="contact-icon"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
              <h3>Instagram</h3>
              <p>@sesamune</p>
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="footer">
        <motion.div
          className="footer-top"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {/* Left: Brand */}
          <motion.div className="footer-brand" variants={fadeInUp}>
            <motion.img
              className="logo-mark"
              src={logo}
              alt="Sesamune Logo"
              whileHover={{ rotate: [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5 }}
            />
          </motion.div>

          {/* Center: Nav and Tagline */}
          <motion.div className="footer-center" variants={fadeInUp}>
            <nav className="footer-nav">
              <motion.a
                href="#home"
                onClick={(e) => handleSmoothScroll(e, '#home')}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                Home
              </motion.a>
              <motion.a
                href="#why"
                onClick={(e) => handleSmoothScroll(e, '#why')}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                Why Sesamune
              </motion.a>
              <motion.a
                href="#benefits"
                onClick={(e) => handleSmoothScroll(e, '#benefits')}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                Benefits
              </motion.a>
              <motion.a
                href="#story"
                onClick={(e) => handleSmoothScroll(e, '#story')}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
              >
                Our Story
              </motion.a>
            </nav>
            <p className="footer-tagline">
              Built on honesty. Backed by tradition. Made for everyday wellness.
            </p>
          </motion.div>

          {/* Right: Invisible Spacer to balance layout */}
          <div className="footer-spacer" aria-hidden="true"></div>
        </motion.div>

        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="footer-bottom-content">
            <span>© 2026 Sesamune. All rights reserved.</span>
            <span className="footer-credit">
              Designed and Developed by <a href="https://brandandbrandz.com" target="_blank" rel="noopener noreferrer">BrandAndBrandz</a>
            </span>
          </div>
        </motion.div>
      </footer>
    </div>
  );
}

export default App;